import { PayloadAction } from '@reduxjs/toolkit';
import { LanguageSelectors } from '@web_opxp/features';
import { AxiosResponse } from 'axios';
import { AnyAction } from 'redux';
import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import * as HistoryService from '../base/historyService';
import getStore from '../store';
import BridgeCache from './cache';
import * as FirebaseActions from './firebaseActions';
import * as Selectors from './selectors';
import BridgeService from './service';
import { BridgeActions } from './slice';
import { IBaseBridgeAction, IBridgeAction, IBridgeTriggerCache } from './types';
import * as Utils from './utils';

export function* initBridgeSaga(action: PayloadAction<number>) {
  const userId = action.payload;
  BridgeService.init(ApiService);
  yield call(BridgeCache.init, userId);
  // register location listener for triggering action on location change
  const listener = () => {
    const store = getStore();
    store.dispatch(BridgeActions.detectBridgeAction());
  };
  HistoryService.addListener(listener);
}

export function* getBridgeActionsSaga() {
  if (!BridgeCache.isReady()) {
    throw new Error('Bridge cache not ready!');
  }
  // check cache
  let activeWebActions = yield call(BridgeCache.getActions);
  // if cache is empty or outdated - fetch new actions
  if (!activeWebActions) {
    activeWebActions = yield call(fetchActions);
    // update the cache
    yield call(BridgeCache.setActions, activeWebActions);
  }

  // if no actions - do nothing
  if (activeWebActions.length === 0) {
    return;
  }

  // sort received actions
  const sortedActions = Utils.sortBridgeActionsPriority(activeWebActions);
  yield put(BridgeActions.updateBridgeActions(sortedActions));

  // start detection if any bridge must be shown
  yield put(BridgeActions.detectBridgeAction());
}

export function* fetchActions() {
  try {
    const result: AxiosResponse<IBridgeAction[]> = yield call([BridgeService, 'getBridgeActions']);
    const rawActions = result.data;
    const actions = Utils.getOpxpActions(rawActions);
    return actions;
  } catch (error) {
    return [];
  }
}

export function* detectActiveActionSaga() {
  if (!BridgeCache.isReady()) {
    throw new Error('Bridge cache not ready!');
  }
  // wait if actions are not loaded
  const isLoaded = yield select(Selectors.isBridgeLoaded);
  if (!isLoaded) {
    yield take(BridgeActions.updateBridgeActions);
  }
  // do nothing if there is already an active action present
  const activeAction = yield select(Selectors.getCurrentActionFilterId);
  if (activeAction) {
    return;
  }
  // do nothing if no actions present
  const actions = yield select(Selectors.getBridgeActions);
  if (!actions.length) {
    return;
  }
  // check for page triggers
  const page = yield call(HistoryService.getCurrentPath);
  const actionToTrigger = Utils.findActiveActionForPage(page, actions);
  if (!actionToTrigger) {
    // there are no actions to be triggered
    return;
  }
  const parameterRequired = Utils.doesActionHaveParameterRequirement(actionToTrigger);
  if (parameterRequired) {
    const requiredParamName = Utils.getTriggerParameterName(actionToTrigger);
    if (!requiredParamName) {
      // parameter name set incorrectly
      // ignore action
      return;
    }
    const requiredParamValue = Utils.getTriggerParameterValue(actionToTrigger);
    const actualParamValue = yield select(Selectors.getParameterValue, requiredParamName);
    if (actualParamValue !== requiredParamValue) {
      // requirement not met
      // ignore action
      return;
    }
  }
  // check cache for triggers of this action
  const triggerCache: IBridgeTriggerCache = yield call(BridgeCache.getTriggers);
  const filterid = Utils.pickFilterId(actionToTrigger);
  const actionTrigger = triggerCache[filterid];
  // check for times triggered
  const triggeredEnough = Utils.isActionTriggeredEnough(actionToTrigger, actionTrigger);
  if (triggeredEnough) {
    // action was already triggered enough times
    return;
  }
  // check for the timeout since last trigger
  const triggeredRecently = Utils.isActionTriggeredRecently(actionToTrigger, actionTrigger);
  if (triggeredRecently) {
    return;
  }
  // check for users language present in the bridge
  const language = yield select(LanguageSelectors.getLanguage);
  const action = Utils.pickAction(actionToTrigger);
  const namespaceAvailable = Utils.isNamespaceDefined(action, language);
  if (!namespaceAvailable) {
    // skip action if can not render
    // track event
    yield put(FirebaseActions.trackNoNamespaceBridgeSkip(filterid, language));
    // mark action as triggered
    yield call(triggerAction, filterid);
    // remove action for the stack
    yield put(BridgeActions.removeBridgeAction(filterid));
    yield put(BridgeActions.detectBridgeAction());
    return;
  }
  yield put(BridgeActions.activateBridgeAction(filterid));
  yield put(FirebaseActions.trackBridgeOpen(filterid));
}

export function* handleSkipActionSaga(action: PayloadAction<number>) {
  const filterid = action.payload;
  const language = yield select(LanguageSelectors.getLanguage);
  const bridgeAction: IBaseBridgeAction | undefined = yield select(Selectors.findActionByFilterId, filterid);
  if (!bridgeAction) {
    return;
  }
  const namespaceAvailable = Utils.isNamespaceDefined(bridgeAction, language);
  if (namespaceAvailable) {
    yield put(FirebaseActions.trackBridgeSkip(filterid));
  } else {
    yield put(FirebaseActions.trackNoNamespaceBridgeSkip(filterid, language));
  }
  yield call(triggerAction, filterid);
  yield put(BridgeActions.removeBridgeAction(filterid));
  yield put(BridgeActions.detectBridgeAction());
}

export function* triggerAction(filterid: number) {
  if (!BridgeCache.isReady()) {
    throw new Error('Bridge cache not ready!');
  }
  const triggerBase: IBridgeTriggerCache = yield call(BridgeCache.getTriggers);
  if (!triggerBase[filterid]) {
    triggerBase[filterid] = Utils.createNewTrigger(filterid);
  }
  triggerBase[filterid].times += 1;
  triggerBase[filterid].triggeredAt = Date.now();
  yield call(BridgeCache.setTriggers, triggerBase);
}

export function* handleBridgeActionCtaSaga(action: PayloadAction<number>) {
  const filterid = action.payload;
  yield put(FirebaseActions.trackBridgeCta(filterid));
  const bridgeAction = yield select(Selectors.findActionByFilterId, filterid);
  const ctaActionType = Utils.getBridgeCta(bridgeAction);
  const ctaActionPayload = Utils.getBridgeCtaPayload(bridgeAction);
  const ctaAction: AnyAction = { type: ctaActionType };
  if (ctaActionPayload) {
    ctaAction.payload = ctaActionPayload;
  }
  yield call(completeAction, filterid);
  yield put(BridgeActions.removeBridgeAction(filterid));
  yield put(ctaAction);
}

export function* completeAction(filterid: number) {
  try {
    yield call([BridgeService, 'completeBridge'], filterid);
    const triggerBase: IBridgeTriggerCache = yield call(BridgeCache.getTriggers);
    // delete trigger base entry as there should be no such action anymore
    if (triggerBase[filterid]) {
      delete triggerBase[filterid];
      yield call(BridgeCache.setTriggers, triggerBase);
    }
    // clean cache
    yield call(BridgeCache.resetActions);
    // fetch actions again
    yield put(BridgeActions.requestBridge());
  } catch (error) {
    // close action
    yield put(BridgeActions.closeBridgeAction(filterid));
  }
}

export default function* bridgeWatcher() {
  yield all([
    takeEvery(BridgeActions.initBridge, initBridgeSaga),
    takeEvery(BridgeActions.requestBridge, getBridgeActionsSaga),
    takeLatest(BridgeActions.detectBridgeAction, detectActiveActionSaga),
    takeEvery(BridgeActions.closeBridgeAction, handleSkipActionSaga),
    takeEvery(BridgeActions.ctaBridgeAction, handleBridgeActionCtaSaga),
  ]);
}
